import { formatPlaceName } from '@/lib/utils';

/**
 * Configuration options for chart scale calculation
 */
export interface ScaleConfig {
  /** Number of ticks to generate (default: 5) */
  tickCount?: number;
  /** Padding as a percentage of the data range (default: 0.1 = 10%) */
  padding?: number;
  /** Force the minimum value to be 0 (default: false) */
  forceZeroMin?: boolean;
  /** Type of data being displayed (default: 'unit') */
  dataType?: 'percentage' | 'ratio' | 'unit' | 'year' | 'custom';
  /** Custom formatter for tick labels */
  customFormatter?: (value: number) => string;
  /** Minimum value for the scale */
  min?: number;
  /** Maximum value for the scale */
  max?: number;
  /** Force scale to include zero (default: false) */
  includeZero?: boolean;
  /** Enforce minimum value of 0 for the scale (default: false) */
  enforceMinZero?: boolean;
}

/**
 * Result of chart scale calculation
 */
export interface ScaleResult {
  /** Array of tick values and their formatted labels */
  ticks: Array<{
    value: number;
    label: string;
  }>;
  /** Calculated minimum value of the scale */
  min: number;
  /** Calculated maximum value of the scale */
  max: number;
  /** Indicates if the scale has negative values */
  hasNegative: boolean;
  /** Indicates if the scale has positive values */
  hasPositive: boolean;
  /** Indicates if the scale crosses zero */
  crossesZero: boolean;
}

/**
 * Calculate nice round numbers for chart axis ticks based on data range
 * @param data Array of numeric values to scale
 * @param config Scale configuration options
 * @returns Scale calculation result with ticks and range
 */
export function calculateChartScale(
  data: number[],
  config: ScaleConfig = {}
): ScaleResult {
  // Set defaults
  const {
    tickCount = 5,
    padding = 0.0,
    forceZeroMin = false,
    dataType = 'unit',
    customFormatter,
    includeZero = false,
    enforceMinZero = false,
    min: configMin,
    max: configMax
  } = config;

  // Handle empty data case
  if (!data.length) {
    return {
      ticks: [{ value: 0, label: '0' }],
      min: 0,
      max: 0,
      hasNegative: false,
      hasPositive: false,
      crossesZero: false
    };
  }

  // Calculate initial range
  let max = configMax !== undefined ? configMax : Math.max(...data);
  let min = configMin !== undefined ? configMin : Math.min(...data);

  // If enforceMinZero is true or it's percentage/unit data with forceZeroMin, ensure min is never below 0
  if (enforceMinZero || (forceZeroMin && (dataType === 'percentage' || dataType === 'unit'))) {
    min = Math.max(0, min);
  }

  // Handle zero crossing if needed
  const hasNegative = min < 0;
  const hasPositive = max > 0;
  const crossesZero = hasNegative && hasPositive;
  
  if (includeZero || crossesZero) {
    // Ensure zero is included in the range
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }

  const range = max - min;

  // Handle single data point case (range = 0)
  if (range === 0) {
    // Create sensible fallback ranges based on data type and value
    const singleValue = max;
    let fallbackMin: number, fallbackMax: number;
    
    if (dataType === 'percentage') {
      // For percentages, create a range around the value
      const spread = Math.max(1, Math.abs(singleValue) * 0.2); // 20% spread, minimum 1%
      fallbackMin = Math.max(enforceMinZero ? 0 : -100, singleValue - spread);
      fallbackMax = Math.min(100, singleValue + spread);
    } else if (dataType === 'ratio') {
      // For ratios (like price-to-income), create reasonable range
      const spread = Math.max(0.5, singleValue * 0.25); // 25% spread, minimum 0.5x
      fallbackMin = Math.max(0, singleValue - spread);
      fallbackMax = singleValue + spread;
    } else if (dataType === 'year') {
      // For years, create 10-year range
      fallbackMin = singleValue - 5;
      fallbackMax = singleValue + 5;
    } else {
      // For units and custom types, create range based on value magnitude
      if (singleValue === 0) {
        fallbackMin = enforceMinZero ? 0 : -1;
        fallbackMax = 1;
      } else {
        const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(singleValue))));
        const spread = Math.max(magnitude * 0.5, Math.abs(singleValue) * 0.3);
        fallbackMin = Math.max(enforceMinZero ? 0 : Number.NEGATIVE_INFINITY, singleValue - spread);
        fallbackMax = singleValue + spread;
      }
    }
    
    min = fallbackMin;
    max = fallbackMax;
  }

  const finalRange = max - min;

  // Add padding to range
  const paddingValue = finalRange * padding;
  const effectiveMin = min - (paddingValue / 2);
  const effectiveMax = max + (paddingValue / 2);
  
  // Calculate step size for ticks
  const roughStep = (effectiveMax - effectiveMin) / (tickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(roughStep))));
  
  // Find a nice step size that's a multiple of 2, 5, or 10
  let niceStep: number;
  const normalizedStep = roughStep / magnitude;
  if (normalizedStep <= 1) niceStep = magnitude;
  else if (normalizedStep <= 2) niceStep = 2 * magnitude;
  else if (normalizedStep <= 5) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  // Adjust the minimum and maximum values to be multiples of the step size
  let adjustedMin = Math.floor(effectiveMin / niceStep) * niceStep;
  let adjustedMax = Math.ceil(effectiveMax / niceStep) * niceStep;

  // If enforcing zero minimum, ensure adjusted min doesn't go below 0
  if (enforceMinZero || (forceZeroMin && (dataType === 'percentage' || dataType === 'unit'))) {
    adjustedMin = Math.max(0, adjustedMin);
  }

  // Generate ticks
  const tickStep = (adjustedMax - adjustedMin) / (tickCount - 1);
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const value = Number((adjustedMin + (tickStep * i)).toFixed(4));
    
    // Format the label based on data type
    let label: string;
    if (customFormatter) {
      label = customFormatter(value);
    } else {
      switch (dataType) {
        case 'percentage':
          // For percentages, show one decimal place only if needed
          label = `${Math.abs(value) < 10 ? value.toFixed(1) : Math.round(value)}%`;
          break;
        case 'ratio':
          label = `${value.toFixed(1)}x`;
          break;
        case 'year':
          label = `${Math.round(value)}`;
          break;
        case 'unit':
          label = new Intl.NumberFormat().format(Math.round(value));
          break;
        default:
          // For other types, show one decimal place only if needed
          label = `${Math.abs(value) < 10 ? value.toFixed(1) : Math.round(value)}`;
      }
    }

    return { value, label };
  });

  return {
    ticks,
    min: adjustedMin,
    max: adjustedMax,
    hasNegative: adjustedMin < 0,
    hasPositive: adjustedMax > 0,
    crossesZero: adjustedMin < 0 && adjustedMax > 0
  };
} 